import React, { useContext, useState, useEffect, useMemo } from 'react'

import {
  Card,
  Divider,
  Row,
  Col,
  LockIcon,
  DropdownMenu,
  Radio,
  PAYPAL_THEME,
  BodyText,
  TextInput,
} from '@paypalcorp/pp-react'
import { getCurrencyFormat, getLocalizedText } from 'ppbl/utils/locale-helper'
import {
  calculateSelectedOfferValue,
  isApplicationAutomationEligible,
  roundTo6,
} from 'ppbl/utils/clever-utils'
import { LOAN_MAX_TERM, LOAN_MIN_TERM } from 'ppbl/utils/cleverConstants'
import { interpolate } from 'ppbl/utils/string-helper'
import { CleverContext } from './cleverContext'
import { financialCalculatorService } from '../../services/financialCalculator.service'
import { toPercentage } from 'ppbl/utils/common-utils'
import {
  composeValidatorWithValue,
  valRange,
} from 'ppbl/utils/validation-helper'
import { toCommaSeparatedNumber } from '../__utils__'

import './styles.css'

type LoanInfoProp = {
  label: string
  value: string
}

type InterestInputProp = {
  value: number
  onChange: (input: number) => void
  index: number
}

const LoanInfo = ({ label, value }: LoanInfoProp) => {
  return (
    <div className="loan-info-container">
      <span style={{ flex: '1', textAlign: 'left' }}>{label}</span>{' '}
      <span style={{ flex: '1', textAlign: 'right' }}>{value}</span>
    </div>
  )
}

const InterestInput = ({ value, onChange, index }: InterestInputProp) => {
  const percentValidation = (input) => {
    return composeValidatorWithValue({
      label: getLocalizedText(
        'cleverCalculator-page.cleverTermCard.interestRate.label',
      ),
      validators: [valRange(0, 100)],
      value: input,
    })
  }

  const [showValidation, setShowValidation] = useState(percentValidation(value))

  const handleChange = (input) => {
    if (input.length <= 7) {
      const validation = percentValidation(input)

      setShowValidation(validation)

      onChange(input)
    }
  }
  return (
    <div className="loan-info-container">
      <span className="interest-input-label">
        {getLocalizedText(
          'cleverCalculator-page.cleverTermCard.interestRate.name',
        )}
      </span>{' '}
      <span className="interest-input">
        <TextInput
          name="error"
          value={value.toString()}
          data-testid={`interest-input-${index + 1}`}
          onChange={(e) => handleChange(e.target.value)}
          errorText={showValidation}
          label=""
          suffix="%"
          type="number"
        />
      </span>
    </div>
  )
}

const CleverTermCard = ({
  initialTerm,
  setOptionSelected,
  optionSelected,
  index,
}) => {
  const { sysColorSelectedMain, sysColorUnselectedMain } = PAYPAL_THEME

  const {
    loanTerm,
    setLoanTerm,
    offerMaxAmount,
    loanAmount,
    customOfferConstraints,
    averageMonthlyGrossSales,
    updateOfferValues,
    application,
    disableContinue,
    opportunityId,
  } = useContext(CleverContext)

  function formattedCurrency(value) {
    if (isNaN(value)) {
      return '-'
    }
    return getCurrencyFormat(Number(value))
  }

  const radioOptionName = `offer-option-${index + 1}`
  const [currentLoanTerm, setCurrentLoanTerm] = useState(initialTerm)
  const [currentOfferValue, setCurrentOfferValue] = useState<any>({})
  const termArray = Array.from(
    { length: LOAN_MAX_TERM - LOAN_MIN_TERM + 1 },
    (_, i) => i + 17,
  )
  const [totalInterestPercentage, setTotalInterestPercentage] = useState(0)
  const isAutomationEligible = isApplicationAutomationEligible(
    application?.tags,
  )

  const termOptions = termArray.map((term) => ({
    primaryText: `${term} Week`,
    value: `${term}`,
  }))

  const handleDropDownChange = (e) => {
    setCurrentLoanTerm(e.target.value)
  }

  const isEnabled = useMemo(
    () =>
      currentOfferValue?.isQualified &&
      Number(loanAmount) <= Number(currentOfferValue?.maxAmount),
    [currentOfferValue],
  )

  const getActiveStyle = () => {
    const greenBorderHighlight = {
      border: '1.5px solid lightgreen',
      boxShadow: '0 0 5px lightgreen',
    }

    const showAutomationEligibleHighlight =
      isAutomationEligible === 'Y' &&
      currentOfferValue?.maxAutoQualifiedAmount &&
      Number(loanAmount) <= Number(currentOfferValue?.maxAutoQualifiedAmount)

    if (!isEnabled) {
      return { borderColor: sysColorUnselectedMain, borderWidth: '2.5px' }
    }
    return optionSelected === radioOptionName
      ? {
          ...(showAutomationEligibleHighlight
            ? greenBorderHighlight
            : { borderColor: sysColorSelectedMain, borderWidth: '2.5px' }),
        }
      : {}
  }

  const getTotalRepaymentAmount = () => {
    if (currentOfferValue?.offerAmount && currentOfferValue?.financeCharges) {
      return getCurrencyFormat(
        currentOfferValue.offerAmount + currentOfferValue.financeCharges,
      )
    }
    return '-'
  }

  useEffect(() => {
    const weeklyInterestRate = (financialCalculatorService.getWeeklyInterestRate(
      loanTerm === currentLoanTerm ? totalInterestPercentage : 0,
      currentLoanTerm,
    ))

    let offerInfoValue = calculateSelectedOfferValue(
      opportunityId,
      Number(offerMaxAmount),
      Number(currentLoanTerm),
      Number(loanAmount),
      customOfferConstraints,
      averageMonthlyGrossSales,
      weeklyInterestRate > 0 ? weeklyInterestRate : undefined,
    )
    setCurrentOfferValue(offerInfoValue)

    const totalInterestInPercentage = toPercentage(
      offerInfoValue.totalInterestPercentage,
    )
    setTotalInterestPercentage(totalInterestInPercentage)

    // update selection if it is the selected term
    if (optionSelected === radioOptionName) {
      setLoanTerm(currentLoanTerm)
      updateOfferValues(offerInfoValue)
      disableContinue(!isEnabled)
    }
  }, [offerMaxAmount, currentLoanTerm, loanAmount, optionSelected, isEnabled])

  // when interest changes, recalculate the offer value
  useEffect(() => {
    if (totalInterestPercentage !== 0) {
      const weeklyInterestRate =
        (financialCalculatorService.getWeeklyInterestRate(
          totalInterestPercentage,
          currentLoanTerm,
        ))

      let offerInfoValue = calculateSelectedOfferValue(
        opportunityId,
        Number(offerMaxAmount),
        Number(currentLoanTerm),
        Number(loanAmount),
        customOfferConstraints,
        averageMonthlyGrossSales,
        weeklyInterestRate,
      )
      setCurrentOfferValue(offerInfoValue)

      if (optionSelected === radioOptionName) {
        setLoanTerm(currentLoanTerm)
        updateOfferValues(offerInfoValue)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalInterestPercentage])

  return (
    <div className="termCardWrapper">
      {isEnabled ? (
        <Radio
          id={radioOptionName}
          data-testid={radioOptionName}
          name="selectedOffer"
          onChange={() => setOptionSelected(radioOptionName)}
          checked={optionSelected === radioOptionName}
          label={getLocalizedText(
            `cleverCalculator-page.cleverTermCard.${
              optionSelected === radioOptionName
                ? 'radioOptionSelected'
                : 'radioOptionNotSelect'
            }`,
          )}
        />
      ) : (
        <div
          className="disbledRadioButton"
          data-testid={`offer-lock-${index + 1}`}
        >
          <LockIcon
            className="lockIcon"
            size="sm"
            color="sysColorBackgroundMediumContrast"
          />
          <BodyText>
            {interpolate(
              getLocalizedText(
                'cleverCalculator-page.cleverTermCard.radioLockedText',
              ),
              {
                limitAmount: (
                  <b>{`$${toCommaSeparatedNumber(
                    currentOfferValue?.maxAmount,
                  )}`}</b>
                ),
              },
            )}
          </BodyText>
        </div>
      )}
      <Card
        data-testid={`offer-card-${index + 1}`}
        style={{ ...getActiveStyle() }}
      >
        <Row className="termCardHeaderRow">
          <Col xs={6}>
            <DropdownMenu
              id={`withDefaultSelectedValue-${index + 1}`}
              data-testid={`withDefaultSelectedValue-${index + 1}`}
              label=""
              options={termOptions}
              value={currentLoanTerm}
              onChange={handleDropDownChange}
              menuContainerClassName="term-dropdown"
            />
          </Col>
          <Col className="termCardAmountCol">
            {isAutomationEligible === 'Y' && (
              <>
                <div>
                  {getLocalizedText(
                    'cleverCalculator-page.cleverTermCard.radioCardHeading1',
                  )}
                </div>
                <div>
                  {toCommaSeparatedNumber(
                    Number(currentOfferValue?.maxAutoQualifiedAmount),
                  )}
                </div>
              </>
            )}
          </Col>
          <Col className="termCardAmountCol ">
            <div className="termCardMaxAmountCol">
              <div>
                {getLocalizedText(
                  'cleverCalculator-page.cleverTermCard.radioCardHeading2',
                )}
              </div>
              <div>
                {toCommaSeparatedNumber(Number(currentOfferValue?.maxAmount))}
              </div>
            </div>
          </Col>
        </Row>
        <Card.Content data-testid={`offer-cardContents-${index + 1}`}>
          <LoanInfo
            label={getLocalizedText(
              'cleverCalculator-page.cleverTermCard.radioInfoMetricLabel1',
            )}
            value={formattedCurrency(currentOfferValue?.weeklyPayment)}
          />
          <Divider />
          <LoanInfo
            label={getLocalizedText(
              'cleverCalculator-page.cleverTermCard.radioInfoMetricLabel2',
            )}
            value={formattedCurrency(currentOfferValue?.totalInterestCharge)}
          />
          <Divider />
          <LoanInfo
            label={getLocalizedText(
              'cleverCalculator-page.cleverTermCard.radioInfoMetricLabel3',
            )}
            value={getTotalRepaymentAmount()}
          />
          <Divider />
          <InterestInput
            value={totalInterestPercentage}
            onChange={setTotalInterestPercentage}
            index={index}
          />
        </Card.Content>
      </Card>
    </div>
  )
}

export default CleverTermCard
